<template>
  <div class="CitySearch">
    <GamSimpleLocationSearch v-bind="getSimpleLocationSearch" />
  </div>
</template>

<script lang="ts" setup>
import { gambits } from '@/core/gambits';
import LocationHelper from '@/core/helpers/location.helper';
import { createLogger } from '@/core/helpers/logger.helper';
import type { Result } from '@/core/network/http/httpClient.interface';
import { useCityStore } from '@/stores/city.store';
import GamSimpleLocationSearch from '@/views/components/GamSimpleLocationSearch.vue';
import { GamDropdownCategory, GamDropdownSize } from '@/views/composables/constants/components/gamDropdown.constants';
import { GamIconName } from '@/views/composables/constants/components/gamIcon.constants';
import { GamInputName } from '@/views/composables/constants/components/gamInput.constants';
import { GamListId } from '@/views/composables/constants/components/gamIntersect.constants';
import type { GamDropdownItem } from '@/views/composables/models/components/GamDropdown';
import type { GamSimpleInputSearchType } from '@/views/composables/models/components/GamInput';
import { storeToRefs } from 'pinia';
import { computed, nextTick } from 'vue';

const logger = createLogger('CitySearch');

// PROPS

export interface SparkMatchCityProps {
  onChange?: (item: GamDropdownItem | null) => void;
  maxItems?: number;
  /** The empty map pin beside the city name. */
  withPin?: boolean;
}

const props = defineProps<SparkMatchCityProps>();

// STORES

const cityStore = useCityStore();
const { city } = storeToRefs(cityStore);

// COMPUTED

const getSimpleLocationSearch = computed((): GamSimpleInputSearchType<GamDropdownItem> => {
  return {
    id: GamListId.SPARK_CITIES,
    loadOnInit: false,
    input: {
      name: GamInputName.SEARCH,
      placeholder: 'IRL CITY',
      value: city.value?.name || '',
      canClear: false,
      isHighlit: false,
      ...(props.withPin && { leftIcon: GamIconName.LOCATION_EMPTY }),
    },
    dropdown: {
      id: GamListId.SPARK_CITIES,
      category: GamDropdownCategory.LOCATIONS,
      size: GamDropdownSize.LARGE,
      itemSpacing: 0,
      reversed: false,
      ...(props.maxItems != null && { maxItems: props.maxItems }),
    },
    selectCallback: async (item: GamDropdownItem | null) => {
      logger.info(`Selected: ${JSON.stringify(item)}`);

      props.onChange?.(item);
    },
    loadCallback: async (query: string): Promise<Result<GamDropdownItem[]>> => {
      const result = await gambits.locationService.searchLocationsByQuery(query, {
        mapbox: true,
        internal: false,
        onlyCities: true,
      });

      return {
        ...result,
        data: LocationHelper.mapSearchLocations(result.data),
      };
    },
    onQueryChanged: (query) => {
      logger.info(`Query: ${query}`);
    },
    onMounted: () => {
      logger.info('Mounted');
    },
    onClickClear: () => {
      nextTick(() => {
        const input = document.querySelector('input[name="search-input"]') as HTMLInputElement;
        if (!input) return;
        input.focus();
      });
    },
  };
});
</script>

<style lang="scss" scoped>
.CitySearch {
  width: 100%;
  // max-width: 15rem;
}

.CitySearch :deep(.right-fade) {
  display: none;
}
</style>
